import { h, RefObject } from 'preact';
import { StateUpdater, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import CheckIcon from '../../assets/CheckIcon.svg';
import EllipseClicked from '../../assets/EllipseClicked.svg';
import EllipseDefault from '../../assets/EllipseDefault.svg';
import EllipseSmall from '../../assets/EllipseSmall.svg';
import EndOfSurveyIcon from '../../assets/ThankYouIcon.svg';
import { PreviewContext } from '../../common/creative/contexts/previewContext';
import { useAdLoaded } from '../../common/creative/hooks/useAdLoaded';
import { useAdParameters } from '../../common/creative/hooks/useAdParameters';
import { useMaxSlotSize } from '../../common/creative/hooks/useMaxSlotSize';
import { useUpdateSlotSize } from '../../common/creative/hooks/useUpdateSlotSize';
import { isRtlLanguage } from '../../common/creative/rtl-languages';
import { CreativeComponent } from '../../common/creative/social/creative-component';
import { LineClamp } from '../../common/creative/social/line-clamp';
import { classNames } from '../../common/css';
import { teadsApi } from '../../common/teads-api';
import { track, trackPixel } from '../../common/tracking';
import { BrandPulseConfig, BrandPulseQuestionConfig } from './config';
import { createAnswer } from './createAnswer';
import classes from './creative.scss';
import { getLayout, Layout, LayoutClassName, LayoutLineHeightAnswer, LayoutLineHeightQuestion } from './layout';

interface AnswerButtonsProps {
  currentQuestion: BrandPulseQuestionConfig;
  answerIndex: string;
  setSelectedAnswers: StateUpdater<string[]>;
  layout: Layout;
  className?: string;
  checked: boolean;
}

interface ProgressBarProps {
  active: boolean;
  showProgress: boolean;
}

export function trackPcpBPClickPixels() {
  teadsApi.getContextData((err, response) => {
    if (err) return console.error(err);
    const { tracking } = response;
    const pcp = tracking?.pcp;
    const bp_click = tracking?.bp_click;

    if (pcp) {
      trackPixel(pcp);
    }

    if (bp_click) {
      trackPixel(bp_click);
    }
  });
}

export function ProgressBar({ active, showProgress }: ProgressBarProps) {
  const coloredProgressBar = showProgress
    ? active
      ? classes.progressBarColorChecked
      : classes.progressBarColorDefault
    : '';
  return <div className={classNames(classes.progressBar, coloredProgressBar)} />;
}

export function AnswerButton({
  currentQuestion,
  answerIndex,
  checked,
  setSelectedAnswers,
  layout,
  className,
}: AnswerButtonsProps) {
  const multipleAnswerFlag = currentQuestion.multipleAnswers;
  const elem = answerIndex;
  const buttonColor = checked ? classes.buttonClickedColor : classes.buttonColor;
  const checkBoxClass = checked ? classes.checkBoxClicked : classes.checkBoxDefault;

  const handleClick = useCallback(() => {
    setSelectedAnswers((clickedAnswers: string[]) => {
      if (multipleAnswerFlag) {
        return checked
          ? clickedAnswers.filter((v: string) => elem !== v)
          : elem === 'none'
            ? [elem]
            : [...clickedAnswers.filter((v: string) => v !== 'none'), elem];
      } else {
        return checked ? [] : [elem];
      }
    });
  }, [checked, elem, multipleAnswerFlag, setSelectedAnswers]);

  return (
    <div className={classNames(classes.button, classes.answerButton, buttonColor, className)} onClick={handleClick}>
      {multipleAnswerFlag && (
        <div>
          <div className={classNames(classes.checkBox, checkBoxClass)}>{checked && <img src={CheckIcon} />}</div>
        </div>
      )}
      {!multipleAnswerFlag && (
        <div className={classes.radioButton}>
          {checked && (
            <div className={classes.radioButtonClicked}>
              <img src={EllipseClicked} className={classes.imageSize} />
              <img src={EllipseSmall} className={classNames(classes.radioButtonSmallEllipse, classes.imageSizeSmall)} />
            </div>
          )}
          {!checked && <img src={EllipseDefault} className={classes.imageSize} />}
        </div>
      )}
      <LineClamp
        lines={3}
        lineHeight={LayoutLineHeightAnswer[layout]}
        text={currentQuestion.answers[answerIndex as any].text}
        dots
      />
    </div>
  );
}

export interface BrandPulseControls {
  showPanel: (panel: number | 'endpage') => void;
}

interface BrandPulseCreativeProps {
  controls?: RefObject<BrandPulseControls>;
}

export function BrandPulseCreative({ controls }: BrandPulseCreativeProps) {
  const load = useAdLoaded();
  useEffect(() => load(), []);

  const { config } = useAdParameters<BrandPulseConfig>();

  const questionsKeys = useMemo(() => {
    return Object.keys(config.questions)
      .map((value) => parseInt(value, 10))
      .sort((a, b) => a - b);
  }, [config.questions]);
  const questionsCount = questionsKeys.length;
  const [currentQuestionKey, setCurrentQuestionKey] = useState(questionsKeys[0]);
  const currentQuestion =
    config.questions[currentQuestionKey] ??
    config.questions[questionsKeys.find((k) => k > currentQuestionKey) ?? questionsKeys[0]];

  const [selectedAnswers, setSelectedAnswers] = useState<any[]>([]);
  const [userAnswers, setUserAnswers] = useState<any[]>([]);

  const [surveyFinished, setSurveyFinished] = useState(false);

  const showPanel = useCallback((panel: number | 'endpage') => {
    if (panel === 'endpage') {
      setSurveyFinished((prev) => {
        const next = true;
        if (next !== prev) setSelectedAnswers([]);
        return next;
      });
    } else {
      setSurveyFinished(false);
      setCurrentQuestionKey((prev) => {
        if (panel !== prev) setSelectedAnswers([]);
        return panel;
      });
    }
  }, []);

  useEffect(() => {
    if (!controls) return;

    controls.current = {
      showPanel,
    };
  }, [controls, showPanel]);

  const isPreview = useContext(PreviewContext);
  const answers = useMemo(() => {
    const { randomAnswers, answers } = currentQuestion;
    const answerKeys = Object.keys(answers);
    if (!isPreview && randomAnswers) {
      const keysAnswer = answerKeys.filter((value) => value !== 'none');
      const keysAnswerArray = keysAnswer.map((value) => parseInt(value, 10));
      const shuffledArray = keysAnswerArray
        .sort(() => 0.5 - Math.random())
        .map((answer, index) => {
          return '' + answer;
        });
      return [...shuffledArray, ...(answers.none ? ['none'] : [])];
    } else {
      return answerKeys;
    }
  }, [currentQuestion]);
  const answersCount = answers.length;

  const maxSlotSize = useMaxSlotSize();
  const layout = useMemo<Layout>(() => getLayout(maxSlotSize.width), [maxSlotSize.width]);

  useEffect(() => {
    setSelectedAnswers([]);
  }, [currentQuestion.multipleAnswers, currentQuestion.type]);

  useEffect(() => {
    if (!isPreview && surveyFinished) {
      const bpAnswers = createAnswer(userAnswers);
      track(bpAnswers);
      if (config.tracking) {
        trackPixel(config.tracking);
      } else {
        trackPcpBPClickPixels();
      }
    }
  }, [surveyFinished, userAnswers, config.tracking]);

  const isRTL = useCallback(() => isRtlLanguage(config.language), [config]);
  const adRef = useRef<HTMLElement>(null);

  const onHandleButtonClick = useCallback(() => {
    if (selectedAnswers.length === 0) return;

    setUserAnswers([...userAnswers, selectedAnswers]);
    const newIndex = questionsKeys.indexOf(currentQuestionKey) + 1;

    showPanel(newIndex === questionsCount ? 'endpage' : questionsKeys[newIndex]);
  }, [questionsKeys, currentQuestionKey, selectedAnswers, userAnswers, showPanel]);
  const answerButtonType = answersCount > 2 ? classes.smallButton : classes.bigButton;

  const lastQuestion = currentQuestionKey == questionsKeys[questionsCount - 1];
  const showNextButton = questionsCount > 1 && !lastQuestion;
  const submitButtonLabel = showNextButton ? config.nextButtonLabel : config.submitButtonLabel;
  const submitButtonType = answersCount > 4 ? classes.smallButton : classes.bigButton;

  const hasSelectedAnswers = selectedAnswers.length > 0;
  const submitButtonClass = hasSelectedAnswers ? classes.submitButtonActive : classes.submitButtonDisabled;

  useUpdateSlotSize(adRef.current);

  return (
    <CreativeComponent adRef={adRef} isRTL={isRTL} className={LayoutClassName[layout]}>
      <div className={classes.adComponent}>
        {!surveyFinished && (
          <div className={classes.questionAnswer}>
            <div className={classes.questionText}>
              <LineClamp lines={3} lineHeight={LayoutLineHeightQuestion[layout]} text={currentQuestion.text} dots />
            </div>
            <div className={classes.answers}>
              {answers.map((answer) => (
                <AnswerButton
                  currentQuestion={currentQuestion}
                  answerIndex={answer}
                  setSelectedAnswers={setSelectedAnswers}
                  layout={layout}
                  checked={selectedAnswers.includes(answer)}
                  className={answerButtonType}
                />
              ))}
              <div
                className={classNames(classes.submitButton, classes.button, submitButtonClass, submitButtonType)}
                onClick={onHandleButtonClick}
              >
                <LineClamp lines={3} lineHeight={LayoutLineHeightAnswer[layout]} text={submitButtonLabel} dots />
              </div>
            </div>
            <div>
              <div className={classes.progressBarDiv}>
                {questionsKeys.map((question) => (
                  <ProgressBar
                    active={question <= currentQuestionKey}
                    key={question}
                    showProgress={config.showProgress}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
        {surveyFinished && (
          <div className={classes.thankYou}>
            <img src={EndOfSurveyIcon} />
            <div className={classes.thankYouText}>{config.endPageMessage}</div>
          </div>
        )}
      </div>
    </CreativeComponent>
  );
}
